import { useState } from 'react';
import { IconCalendar, IconChecks, IconClockCheck, IconMessage, IconNews, IconPlus, IconSchool, IconTruck, IconUserOff } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { PageHeader } from '@hana/shell';
import { Modal, StateView, useToast, type ScreenState } from '@hana/ui';
import {
  COURSES, LEAVES0, LEAVE_DAYS, LEAVE_STATUS, LEAVE_TYPES, ME,
  courseView, newLeave, punchLabel, type Leave,
} from './profileData';
import './profile.i18n';
import s from './profile.module.css';

const STATES: ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];

function forcedState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return STATES.includes(v as ScreenState) ? (v as ScreenState) : 'ready';
}

function nowHM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Mint-Mov-Phone2 khung "Cá nhân" (giả lập, giữ trong bộ nhớ). Chưa có thẻ Lương chuyến (F.md: lương tài xế để sau). */
export function Profile() {
  const state = forcedState();
  const t = useT();
  const toast = useToast();
  const [punch, setPunch] = useState<{ i: string | null; o: string | null }>({ i: null, o: null });
  const [leaves, setLeaves] = useState<readonly Leave[]>(LEAVES0);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<string>(LEAVE_TYPES[0]);
  const [day, setDay] = useState<string>(LEAVE_DAYS[0]);
  const [why, setWhy] = useState('');
  const [extra, setExtra] = useState<Record<number, number>>({});
  const [inst, setInst] = useState(false);

  const doPunch = () => {
    if (punch.i && punch.o) return;
    const hm = nowHM();
    const prev = punch;
    const next = !punch.i ? { i: hm, o: null } : { i: punch.i, o: hm };
    setPunch(next);
    toast({ msg: `${punchLabel(prev.i, prev.o)} · ${hm}`, undo: () => setPunch(prev) });
  };
  const send = () => {
    const l = newLeave(type, day, why);
    if (!l) return;
    setLeaves((a) => [l, ...a]);
    setOpen(false);
    setWhy('');
    toast({ msg: t('mvp.leaveSent'), undo: () => setLeaves((a) => a.filter((x) => x !== l)) });
  };

  const trip = (n: number, label: string) => (
    <span title={label} aria-label={label}><IconTruck size={16} stroke={1.8} aria-hidden="true" />{t('mvp.trips', { n })}</span>
  );

  return (
    <>
      <PageHeader title={t('mvp.title')} />
      <div className={s.page}>
        <div className={s.me}>
          <span className={s.av} aria-hidden="true">{ME.initial}</span>
          <div><b>{ME.name}</b><div className={s.lb}>{ME.role}</div></div>
        </div>
        {state === 'error' && <div role="status" className={s.net}><b>{t('mvp.off')}</b>{t('mvp.offT')}</div>}
        <StateView
          state={state === 'error' || state === 'noperm' || state === 'empty' ? 'ready' : state}
          skeleton={<>{[0, 1, 2, 3].map((i) => <div key={i} className={s.sk} aria-hidden="true" />)}</>}
        >
          <nav aria-label={t('mvp.short')} className={s.short}>
            <button type="button" onClick={() => toast({ msg: t('mvp.newsToast') })}><b><IconNews size={16} stroke={1.8} aria-hidden="true" />{t('mvp.news')}</b><span>{t('mvp.newsSub')}</span></button>
            <button type="button" onClick={() => toast({ msg: t('mvp.carToast') })}><b><IconTruck size={16} stroke={1.8} aria-hidden="true" />{t('mvp.car')}</b><span>{t('mvp.carSub')}</span></button>
          </nav>

          <div className={s.card}>
            <div className={s.hd}><b>{t('mvp.att')}</b><span>{ME.today}</span></div>
            <div className={s.auto} title={t('mvp.autoTip')}>
              {trip(ME.tripsToday, t('mvp.todayTrips', { n: ME.tripsToday }))}
              <span><IconClockCheck size={16} stroke={1.8} aria-hidden="true" />{t('mvp.byTrip')}</span>
            </div>
            <button type="button" className={s.ghost} onClick={doPunch} disabled={!!(punch.i && punch.o)}><IconClockCheck size={16} stroke={1.8} aria-hidden="true" />{punchLabel(punch.i, punch.o) === 'Chấm công vào' ? 'Chấm công' : punchLabel(punch.i, punch.o)}</button>
            <div className={s.meta}>
              <span title={t('mvp.oct')} aria-label={t('mvp.oct')}><IconCalendar size={14} stroke={1.8} aria-hidden="true" />{ME.month}</span>
              <span title={t('mvp.daysTip', { n: ME.days })} aria-label={t('mvp.daysTip', { n: ME.days })}><IconClockCheck size={14} stroke={1.8} aria-hidden="true" />{t('mvp.days', { n: ME.days })}</span>
              <span title={t('mvp.leaveTip', { n: ME.leaveDays })} aria-label={t('mvp.leaveTip', { n: ME.leaveDays })}><IconUserOff size={14} stroke={1.8} aria-hidden="true" />{t('mvp.leaveShort', { n: ME.leaveDays })}</span>
              <span title={t('mvp.claim')} aria-label={t('mvp.claim')}><IconMessage size={14} stroke={1.8} aria-hidden="true" />{ME.claimNote}</span>
            </div>
          </div>

          <div className={s.card}>
            <div className={s.hd}><b>{t('mvp.leave')}</b><span title={t('mvp.leftTip', { n: ME.leaveLeft })}><b>{String(ME.leaveLeft).replace('.', ',')}</b>{t('mvp.dayUnit')}</span></div>
            <div className={s.cap}>{t('mvp.mine')}</div>
            {leaves.map((l, i) => (
              <div key={`${l.k}${l.d}${i}`} className={s.row}>
                <div><div className={s.k}>{l.k}</div><div className={s.lb}>{l.d}</div></div>
                <span className={s.chip} data-s={l.s}>{LEAVE_STATUS[l.s]}</span>
              </div>
            ))}
            <button type="button" className={s.btn} onClick={() => setOpen(true)}><IconPlus size={16} stroke={1.8} aria-hidden="true" />{t('mvp.create')}</button>
            <button type="button" className={s.ghost} title={t('mvp.fbTip')} onClick={() => toast({ msg: t('mvp.fbToast') })}><IconMessage size={16} stroke={1.8} aria-hidden="true" />{t('mvp.fb')}</button>
          </div>

          <div className={s.card}>
            <b className={s.hd}>{t('mvp.train')}</b>
            {COURSES.map((c, i) => {
              const v = courseView(c, extra[i] ?? 0);
              return (
                <div key={c.k} className={s.course}>
                  <div className={s.hd}><b style={{ fontSize: 16, fontWeight: 'var(--fw-label)' }}>{c.k}</b><span className={s.chip} data-s={v.fin ? 'ok' : 'wait'}>{v.tag}</span></div>
                  <div className={s.bar} role="progressbar" aria-valuenow={v.pct} aria-valuemin={0} aria-valuemax={100}><i data-fin={v.fin ? '1' : '0'} style={{ width: `${v.pct}%` }} /></div>
                  <div className={s.lb}>{v.desc}</div>
                  {!v.fin && (
                    <button type="button" className={s.btn} style={{ height: 52 }} onClick={() => { setExtra((e) => ({ ...e, [i]: (e[i] ?? 0) + 1 })); toast({ msg: t('mvp.lessonDone', { n: v.next }), undo: () => setExtra((e) => ({ ...e, [i]: Math.max(0, (e[i] ?? 0) - 1) })) }); }}><IconSchool size={16} stroke={1.8} aria-hidden="true" />{t('mvp.learn', { n: v.next })}</button>
                  )}
                </div>
              );
            })}
          </div>

          <div className={s.card}>
            <b className={s.hd}>{t('mvp.acct')}</b>
            <button type="button" className={s.ghost} style={{ height: 52 }} onClick={() => { setInst(true); toast({ msg: t('mvp.installed') }); }}>{inst && <IconChecks size={16} stroke={1.8} aria-hidden="true" />}{inst ? t('mvp.installed') : t('mvp.install')}</button>
          </div>
        </StateView>
      </div>

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('mvp.create')}
        footer={<button type="button" className={s.send} disabled={!why.trim()} onClick={send}>{t('mvp.send')}</button>}
      >
        <label className={s.field}><span>{t('mvp.type')}</span><select value={type} onChange={(e) => setType(e.target.value)}>{LEAVE_TYPES.map((x) => <option key={x}>{x}</option>)}</select></label>
        <label className={s.field}><span>{t('mvp.day')}</span><select value={day} onChange={(e) => setDay(e.target.value)}>{LEAVE_DAYS.map((x) => <option key={x}>{x}</option>)}</select></label>
        <label className={s.field}><span>{t('mvp.why')}</span><textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)} /></label>
      </Modal>
    </>
  );
}
